import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Map, Marker, Overlay, ZoomControl } from 'pigeon-maps';

import { CallCenterOverview, fetchCallCenterOverview, InterstellarAccount } from '../api';
import type { AccountHealth, AccountHealthLevel } from '../api';
import { HEALTH_COLORS, HEALTH_LABELS } from '../constants';

interface AccountMapProps {
  accounts: InterstellarAccount[];
  selectedAccount: InterstellarAccount | null;
  onAccountSelect: (account: InterstellarAccount) => void;
  placementMode?: boolean;
  draftLatLng: [number, number] | null;
  onPlacementMapClick?: (lat: number, lng: number) => void;
  healthMap?: Record<number, AccountHealth>;
}

const HEALTH_DESCRIPTIONS: Record<AccountHealthLevel, string> = {
  critical: 'Llamadas en espera sin agentes disponibles, o abandono mayor al 30%.',
  warning: 'Espera promedio > 2 min, abandono > 15%, o más de 3 llamadas en cola.',
  healthy: 'Operación normal. Agentes disponibles y métricas en rangos aceptables.',
  inactive: 'Sin agentes registrados en la cuenta.',
};

interface HoverState {
  account: InterstellarAccount;
  overview: CallCenterOverview | null;
  loading: boolean;
  error: boolean;
}

const HOVER_DELAY_MS = 300;

function formatWaitTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

export const AccountMap: React.FC<AccountMapProps> = ({
  accounts,
  selectedAccount,
  onAccountSelect,
  placementMode = false,
  draftLatLng,
  onPlacementMapClick,
  healthMap,
}) => {
  const [mapCenter, setMapCenter] = useState<[number, number]>([18.2208, -66.5901]);
  const [zoom, setZoom] = useState(9);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [showHealthHelp, setShowHealthHelp] = useState(false);
  const [healthFilter, setHealthFilter] = useState<AccountHealthLevel | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const cancelFetch = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearHoverTimer();
      cancelFetch();
    };
  }, [clearHoverTimer, cancelFetch]);

  const handleMarkerEnter = useCallback(
    (account: InterstellarAccount) => {
      if (placementMode) return;
      clearHoverTimer();
      cancelFetch();

      hoverTimerRef.current = setTimeout(() => {
        setHover({ account, overview: null, loading: true, error: false });

        const controller = new AbortController();
        abortRef.current = controller;

        fetchCallCenterOverview(account.id)
          .then((data) => {
            if (!controller.signal.aborted) {
              setHover((prev) =>
                prev && prev.account.id === account.id
                  ? { ...prev, overview: data, loading: false }
                  : prev
              );
            }
          })
          .catch(() => {
            if (!controller.signal.aborted) {
              setHover((prev) =>
                prev && prev.account.id === account.id
                  ? { ...prev, loading: false, error: true }
                  : prev
              );
            }
          });
      }, HOVER_DELAY_MS);
    },
    [placementMode, clearHoverTimer, cancelFetch]
  );

  const handleMarkerLeave = useCallback(() => {
    clearHoverTimer();
    cancelFetch();
    setHover(null);
  }, [clearHoverTimer, cancelFetch]);

  const handleMarkerClick = (account: InterstellarAccount): void => {
    if (placementMode) return;
    handleMarkerLeave();
    onAccountSelect(account);
    const lat = Number(account.latitude);
    const lng = Number(account.longitude);
    setMapCenter([lat, lng]);
    setZoom(10);
  };

  const handleMapClick = (args: { latLng?: [number, number] }): void => {
    if (!placementMode || !onPlacementMapClick || !args.latLng) {
      return;
    }
    const [lat, lng] = args.latLng;
    onPlacementMapClick(lat, lng);
  };

  const tooltipAccount = hover?.account;
  const tooltipLat = tooltipAccount ? Number(tooltipAccount.latitude) : 0;
  const tooltipLng = tooltipAccount ? Number(tooltipAccount.longitude) : 0;

  return (
    <div
      className={
        placementMode ? 'relative h-full min-h-0 cursor-crosshair' : 'relative h-full min-h-0'
      }
    >
      <Map
        center={mapCenter}
        zoom={zoom}
        onBoundsChanged={({ center, zoom: newZoom }) => {
          setMapCenter(center);
          setZoom(newZoom);
        }}
        onClick={placementMode ? handleMapClick : undefined}
      >
        <ZoomControl />
        {accounts
          .filter((a) => {
            const level = healthMap?.[a.id]?.level;
            if (healthFilter && level !== healthFilter) return false;
            return level === 'critical';
          })
          .map((account) => (
            <Overlay
              key={`pulse-${account.id}`}
              anchor={[Number(account.latitude), Number(account.longitude)]}
              offset={[0, 0]}
            >
              <span
                className="absolute bg-red-400 rounded-full"
                style={{
                  width: 40,
                  height: 40,
                  transform: 'translate(-50%, -50%)',
                  animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
                  opacity: 0.4,
                }}
              />
            </Overlay>
          ))}
        {accounts
          .filter((a) => {
            if (!healthFilter) return true;
            return healthMap?.[a.id]?.level === healthFilter;
          })
          .map((account) => {
            const lat = Number(account.latitude);
            const lng = Number(account.longitude);
            const isSelected = selectedAccount?.id === account.id;
            const health = healthMap?.[account.id];
            const markerColor = health
              ? HEALTH_COLORS[health.level]
              : isSelected
              ? '#10b981'
              : '#183161';
            const isCritical = health?.level === 'critical';
            return (
              <Marker
                key={account.id}
                anchor={[lat, lng]}
                onClick={() => handleMarkerClick(account)}
                onMouseOver={() => handleMarkerEnter(account)}
                onMouseOut={handleMarkerLeave}
                color={markerColor}
                width={isSelected ? 40 : isCritical ? 36 : 32}
              />
            );
          })}
        {draftLatLng && (
          <Marker key="draft-georef" anchor={draftLatLng} color="#f59e0b" width={44} />
        )}

        {/* Tooltip overlay on hover */}
        {hover && tooltipAccount && (
          <Overlay anchor={[tooltipLat, tooltipLng]} offset={[0, -48]}>
            <div
              className="pointer-events-none z-50 w-72 rounded-xl border border-white-2.0 bg-white-0.0 shadow-xl"
              style={{ transform: 'translate(-50%, -100%)' }}
            >
              {/* Header */}
              <div className="rounded-t-xl bg-primary-2.0 px-4 py-2.5">
                <p className="text-sm font-bold text-white-0.0 truncate">{tooltipAccount.name}</p>
                <p className="text-[10px] text-white-0.0">ID: {tooltipAccount.id}</p>
              </div>

              {/* Body */}
              <div className="px-4 py-3">
                {hover.loading && (
                  <div className="flex items-center justify-center py-3">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-2.0 border-t-transparent" />
                    <span className="ml-2 text-xs text-gray-2.0">Cargando métricas…</span>
                  </div>
                )}

                {hover.error && !hover.loading && (
                  <p className="py-2 text-center text-xs text-gray-2.0">
                    No se pudieron cargar las métricas
                  </p>
                )}

                {hover.overview && !hover.loading && (
                  <>
                    {/* Agent summary — total, online, offline */}
                    <div className="mb-3">
                      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-2.0">
                        Agentes
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="rounded-lg bg-white-1.0 px-2.5 py-2 text-center">
                          <p className="text-base font-bold text-black-1.0">
                            {hover.overview.agents.total}
                          </p>
                          <p className="text-[10px] font-medium text-gray-2.0">Total</p>
                        </div>
                        <div className="rounded-lg bg-green-50 px-2.5 py-2 text-center">
                          <p className="text-base font-bold text-green-600">
                            {hover.overview.agents.available}
                          </p>
                          <p className="text-[10px] font-medium text-green-600">Online</p>
                        </div>
                        <div className="rounded-lg bg-red-50 px-2.5 py-2 text-center">
                          <p className="text-base font-bold text-red-500">
                            {hover.overview.agents.offline}
                          </p>
                          <p className="text-[10px] font-medium text-red-500">Offline</p>
                        </div>
                      </div>
                    </div>

                    {/* Agent detail badges: busy, ringing */}
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {hover.overview.agents.busy > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                          {hover.overview.agents.busy} ocupados
                        </span>
                      )}
                      {(hover.overview.agents.ringing ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
                          {hover.overview.agents.ringing} sonando
                        </span>
                      )}
                    </div>

                    {/* Call stats grid */}
                    <div className="mb-2">
                      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-2.0">
                        Llamadas hoy
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center">
                          <p className="text-sm font-bold text-black-1.0">
                            {hover.overview.calls_waiting}
                          </p>
                          <p className="text-[10px] text-gray-2.0">En espera</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold text-green-600">
                            {hover.overview.answered_calls_today}
                          </p>
                          <p className="text-[10px] text-gray-2.0">Contestadas</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold text-red-500">
                            {hover.overview.abandoned_calls_today.count}
                            <span className="ml-0.5 text-[10px] font-medium text-red-400">
                              ({hover.overview.abandoned_calls_today.percentage.toFixed(1)}%)
                            </span>
                          </p>
                          <p className="text-[10px] text-gray-2.0">Abandonadas</p>
                        </div>
                      </div>
                    </div>

                    {/* Wait times */}
                    <div className="mt-2 flex gap-4 border-t border-white-2.0 pt-2">
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-2.0">Espera promedio</p>
                        <p className="text-xs font-bold text-black-1.0">
                          {formatWaitTime(hover.overview.average_wait_time_seconds)}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-2.0">Espera máxima</p>
                        <p className="text-xs font-bold text-black-1.0">
                          {formatWaitTime(hover.overview.maximum_wait_time_seconds)}
                        </p>
                      </div>
                    </div>

                    {/* Health indicator — alert when calls waiting but no agents available */}
                    {hover.overview.calls_waiting > 0 && hover.overview.agents.available === 0 && (
                      <div className="mt-2 rounded-md bg-red-50 px-2.5 py-1.5 text-center">
                        <p className="text-[10px] font-semibold text-red-600">
                          Llamadas en espera sin agentes disponibles
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer hint */}
              <div className="rounded-b-xl border-t border-white-2.0 bg-white-1.0 px-4 py-1.5 text-center">
                <p className="text-[10px] text-gray-2.0">Clic para ver detalle</p>
              </div>
            </div>
          </Overlay>
        )}
      </Map>

      {/* Health legend */}
      {healthMap && Object.keys(healthMap).length > 0 && (
        <div
          className="rounded-xl bottom-4 left-4 absolute z-10 bg-white border border-gray-300 shadow-lg"
          style={{ maxWidth: showHealthHelp ? 340 : 200 }}
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold tracking-wide text-gray-600 uppercase">
                Estado operativo
              </p>
              <button
                type="button"
                onClick={() => setShowHealthHelp((v) => !v)}
                className="flex-shrink-0 ml-2 text-[10px] font-semibold text-blue-600 hover:text-blue-800"
              >
                {showHealthHelp ? 'Ocultar' : '?'}
              </button>
            </div>
            <div className="space-y-1">
              {(Object.keys(HEALTH_COLORS) as AccountHealthLevel[]).map((level) => {
                const count = Object.values(healthMap).filter((h) => h.level === level).length;
                if (count === 0 && !showHealthHelp) return null;
                const isActive = healthFilter === level;
                const isDimmed = healthFilter != null && !isActive;
                return (
                  <div key={level}>
                    <button
                      type="button"
                      onClick={() => setHealthFilter(isActive ? null : level)}
                      className={`flex w-full items-center gap-2.5 rounded-md px-1.5 py-1 text-left transition-all ${
                        isActive
                          ? 'bg-gray-100 ring-1 ring-gray-300'
                          : isDimmed
                          ? 'opacity-40 hover:opacity-70'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className="flex-shrink-0 inline-block w-3 h-3 border border-white rounded-full shadow-sm"
                        style={{ backgroundColor: HEALTH_COLORS[level] }}
                      />
                      <span className="flex-1 text-xs font-semibold text-gray-800">
                        {HEALTH_LABELS[level]}
                      </span>
                      <span className="text-xs font-bold text-gray-500">{count}</span>
                    </button>
                    {showHealthHelp && (
                      <p className="mt-0.5 ml-7 text-[10px] leading-tight text-gray-500">
                        {HEALTH_DESCRIPTIONS[level]}
                      </p>
                    )}
                  </div>
                );
              })}
              {healthFilter && (
                <button
                  type="button"
                  onClick={() => setHealthFilter(null)}
                  className="w-full pt-1 text-[10px] font-semibold text-blue-600 hover:text-blue-800"
                >
                  Mostrar todos
                </button>
              )}
            </div>
          </div>
          {showHealthHelp && (
            <div className="px-4 py-2 text-[10px] leading-tight text-gray-400 border-t border-gray-200">
              Datos actualizados cada 30s vía FreeSWITCH. Los umbrales determinan el color de cada
              marcador en el mapa.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
